import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || window.location.origin;

// Get JWT token from localStorage - check multiple locations for compatibility
const getJWT = () => {
  try {
    // Priority: userToken (SaaS users) > jwt (internal users) > adminToken (admin users)
    return localStorage.getItem('userToken') || 
           localStorage.getItem('jwt') || 
           localStorage.getItem('adminToken') || '';
  } catch (e) {
    return '';
  }
};

const tenantApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
tenantApi.interceptors.request.use(
  (config) => {
    const jwt = getJWT();
    console.log(`🔍 Token check for ${config.url}:`, {
      hasToken: !!jwt,
      tokenLength: jwt ? jwt.length : 0,
      tokenStart: jwt ? jwt.substring(0, 20) + '...' : 'none'
    });
    if (jwt) {
      config.headers.Authorization = `Bearer ${jwt}`;
    }
    console.log(`Making ${config.method?.toUpperCase()} request to: ${config.url}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
tenantApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('Tenant API Error:', error.response?.data || error.message);
    if (error.response?.status === 401) {
      // Redirect to login if unauthorized
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const dashboardApi = {
  // Get user context (brand and hotel info) - JWT compatible
  getUserContext: async () => {
    try {
      const response = await tenantApi.get('/api/tenants/dashboard/context');
      return response.data;
    } catch (error) {
      throw new Error(`Failed to get user context: ${error.message}`);
    }
  },

  // Get demand data for user's hotel - JWT compatible
  getDemandData: async (start, end) => {
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      
      const response = await tenantApi.get('/api/tenants/dashboard/demand', { params });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch demand data: ${error.message}`);
    }
  },

  // Get price feed data for user's hotel - JWT compatible
  getPriceFeedData: async (days = 30) => {
    try {
      const response = await tenantApi.get('/api/tenants/dashboard/price-feed', { 
        params: { 
          days,
          _t: Date.now() // Cache busting parameter
        } 
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch price feed data: ${error.message}`);
    }
  },

  // Get demand scores for competitor matrix
  getDemandScores: async (days = 14) => {
    try {
      const response = await tenantApi.get('/api/tenants/dashboard/demand-scores', { 
        params: { 
          days,
          _t: Date.now() // Cache busting parameter
        } 
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch demand scores: ${error.message}`);
    }
  },

  // Helper function to get 90-day demand forecast
  get90DayDemandForecast: async () => {
    try {
      const today = new Date();
      const start = today.toISOString().split('T')[0];
      const endDate = new Date(today);
      endDate.setDate(today.getDate() + 90);
      const end = endDate.toISOString().split('T')[0];
      
      return await dashboardApi.getDemandData(start, end);
    } catch (error) {
      throw new Error(`Failed to get 90-day forecast: ${error.message}`);
    }
  },

  // Helper function to get 30-day price and demand correlation
  get30DayPriceDemandCorrelation: async () => {
    try {
      const today = new Date();
      const start = today.toISOString().split('T')[0];
      const endDate = new Date(today);
      endDate.setDate(today.getDate() + 30);
      const end = endDate.toISOString().split('T')[0];
      
      // Fetch both price and demand data
      const [priceData, demandData] = await Promise.all([
        dashboardApi.getPriceFeedData(30),
        dashboardApi.getDemandData(start, end)
      ]);
      
      return { priceData, demandData };
    } catch (error) {
      throw new Error(`Failed to get 30-day correlation: ${error.message}`);
    }
  },


  // Get competitor hotel room types
  getCompetitorRoomTypes: async (hotelIdentifier) => {
    try {
      const response = await tenantApi.get(`/api/tenants/competitor-hotels/${encodeURIComponent(hotelIdentifier)}/room-types`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch competitor room types: ${error.message}`);
    }
  },

  // Get competitor hotels list
  getCompetitorHotels: async (limit = 20) => {
    try {
      const response = await tenantApi.get(`/api/tenants/competitor-hotels-test?limit=${limit}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch competitor hotels: ${error.message}`);
    }
  },

  // --- Competitor hotel self-service ---

  getMyCompetitorHotels: async () => {
    try {
      const response = await tenantApi.get('/api/tenants/my-competitor-hotels');
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch competitor hotels: ${error.message}`);
    }
  },

  addCompetitorHotel: async ({ ota_hotel_name, ota_hotel_id, ota, hotel_url, scraping_status }) => {
    try {
      const response = await tenantApi.post('/api/tenants/my-competitor-hotels', {
        ota_hotel_name,
        ota_hotel_id,
        ota: ota || 'booking.com',
        hotel_url: hotel_url || undefined,
        scraping_status: scraping_status || 'active',
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  removeCompetitorHotel: async (competitorId) => {
    try {
      const response = await tenantApi.delete(`/api/tenants/my-competitor-hotels/${competitorId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to remove competitor hotel: ${error.message}`);
    }
  },

  searchAvailableCompetitorHotels: async ({ search, location, limit } = {}) => {
    try {
      const params = {};
      if (search) params.search = search;
      if (location) params.location = location;
      if (limit) params.limit = limit;
      const response = await tenantApi.get('/api/tenants/available-competitor-hotels', { params });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to search competitor hotels: ${error.message}`);
    }
  },

  // Get room type price comparison for a specific date
  getRoomTypePriceComparison: async (hotelId, date) => {
    try {
      // Get room mappings first (for compatibility with existing code)
      const mappingsResponse = await tenantApi.get(`/api/tenants/hotels/${hotelId}/room-mappings`);
      const mappings = mappingsResponse.data.room_mappings || [];
      
      // Get price feed data for the specific date (for compatibility)
      const priceResponse = await dashboardApi.getPriceFeedData(30);
      const priceData = priceResponse.items || [];
      
      // Filter price data for the specific date
      const targetDate = new Date(date).toISOString().split('T')[0];
      const dateSpecificPrices = priceData.filter(item => {
        const itemDate = new Date(item.checkin_date).toISOString().split('T')[0];
        return itemDate === targetDate;
      });
      
      return { mappings, priceData: dateSpecificPrices };
    } catch (error) {
      throw new Error(`Failed to get room type price comparison: ${error.message}`);
    }
  },

  // Get room type analysis data using the new endpoint
  getRoomTypeAnalysis: async (roomType, days = 14) => {
    try {
      const response = await tenantApi.get('/api/tenants/dashboard/room-type-analysis', {
        params: {
          room_type: roomType,
          days: days,
          _t: Date.now() // Cache busting parameter
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to get room type analysis: ${error.message}`);
    }
  },

};

// User invitation API
export const userApi = {
  async getUsers() {
    try {
      const response = await tenantApi.get('/api/tenants/users');
      return response.data;
    } catch (error) {
      throw new Error(`Failed to get users: ${error.message}`);
    }
  },

  async getPendingInvitations() {
    try {
      const response = await tenantApi.get('/api/tenants/users/pending-invitations');
      return response.data;
    } catch (error) {
      throw new Error(`Failed to get pending invitations: ${error.message}`);
    }
  },

  async inviteUser(data) {
    try {
      const response = await tenantApi.post('/api/tenants/users/invite', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async cancelInvitation(userId) {
    try {
      const response = await tenantApi.delete(`/api/tenants/users/${userId}/cancel-invitation`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to cancel invitation: ${error.message}`);
    }
  },

  async getUserDetails(userId) {
    try {
      const response = await tenantApi.get(`/api/tenants/users/${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to get user details: ${error.message}`);
    }
  }
};

export default tenantApi;
